import { AppSelect } from '../../ui/AppSelect';
import React, { useEffect, useState, useRef } from "react";
import {
  Loader2, Sparkles, Upload, BookOpen, FileText, Link2, Film, Plus, RefreshCw,
  Rows3, PlayCircle,
} from "lucide-react";
import mammoth from "mammoth";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { useToast } from "../../../context/ToastContext";

// 剧本转分镜节点编辑面板
// 数据流：
//   文本来源（直接输入/导入文件/项目剧本）→ data.scriptText（onUpdate 持久化）
//   智能匹配 → data.onSmartMatch(nodeId)（NodeCanvas 实现：拉工坊列表 → 精确匹配 → 建参考节点+连线）
//   生成故事板粗稿 → data.onGenerateMasterShots(nodeId, { aspectRatio })（NodeCanvas 实现：任务+轮询+落画布）
//   补必要连接镜 → data.onGenerateConnectingShots(nodeId)（存在主镜后可用）
//   新建空白镜头 → data.onCreateBlankStoryboardShot(nodeId, { aspectRatio })（人工从零起稿）
const ACCENT = "var(--color-accent-primary)";
const accentBg = (color, pct = 12) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;
const ASPECT_RATIOS = ["16:9", "21:9", "9:16", "1:1", "4:3", "3:4"];

const TABS = [
  { key: "input", label: "直接输入", Icon: FileText },
  { key: "file", label: "导入文件", Icon: Upload },
  { key: "project", label: "项目剧本", Icon: BookOpen },
];

const ASSET_TYPE_LABEL = { character: "角色", scene: "场景", prop: "道具", other: "其他" };
const ASSET_TYPE_COLOR = {
  character: "var(--color-blue-primary)",
  scene: "var(--color-green-primary)",
  prop: "var(--color-pink-primary)",
  other: "var(--color-text-muted)",
};

const episodeLabel = (ep) => {
  const order = Number(ep?.sortOrder || ep?.episodeNumber || 0);
  const title = String(ep?.title || "").trim();
  return title ? `第${order || "?"}集 ${title}` : `第${order || "?"}集`;
};

export default function ScriptToStoryboardPanel({ data, id, onClose }) {
  const toast = useToast();
  const projectId = data.projectId || null;
  const fileRef = useRef(null);

  const [tab, setTab] = useState(data.scriptSource || "input");
  const [text, setText] = useState(data.scriptText || "");
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || "16:9");
  const [episodes, setEpisodes] = useState(null); // null = 未加载
  const [epLoading, setEpLoading] = useState(false);
  const [episodeChoice, setEpisodeChoice] = useState(data.scriptEpisodeId || "");
  const [parsing, setParsing] = useState(false);

  // 任务恢复或外部载入剧本时同步本地编辑态，避免在渲染阶段触发 setState。
  useEffect(() => {
    setText(data.scriptText || "");
    setAspectRatio(data.aspectRatio || "16:9");
  }, [data.scriptText, data.aspectRatio]);

  const isGenerating = !!data.isGenerating;
  const activeTaskId = data.activeTaskId || "";
  const hasActiveTask = !!activeTaskId;
  const taskPaused = hasActiveTask && !isGenerating;
  const smartMatching = !!data.smartMatching;
  const syncingShotReferences = !!data.syncingShotReferences;
  const masterCount = Array.isArray(data.masterShotNodeIds) ? data.masterShotNodeIds.length : 0;
  const progress = typeof data.generateProgress === "number" ? data.generateProgress : null;

  const persist = (patch) => data.onUpdate?.(id, patch);

  const applyText = (value, sourcePatch = {}) => {
    setText(value);
    persist({ scriptText: value, ...sourcePatch });
  };

  const loadEpisodes = async () => {
    if (!projectId) {
      toast.warning("缺少项目上下文，无法加载项目剧本");
      return;
    }
    setEpLoading(true);
    try {
      const res = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
      const list = Array.isArray(res) ? [...res] : [];
      list.sort((a, b) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)) || (Number(a?.id || 0) - Number(b?.id || 0)));
      setEpisodes(list);
    } catch (e) {
      console.error("[ScriptToStoryboard] 加载分集列表失败:", e);
      toast.error("加载分集列表失败");
      setEpisodes([]);
    } finally {
      setEpLoading(false);
    }
  };

  const switchTab = (key) => {
    setTab(key);
    persist({ scriptSource: key });
    if (key === "project" && episodes === null && !epLoading) {
      loadEpisodes();
    }
  };

  // 导入文件：.docx 走 mammoth，.txt/.md 直接读
  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      let content = "";
      if (/\.docx$/i.test(file.name)) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        content = result.value || "";
      } else if (/\.(txt|md)$/i.test(file.name)) {
        content = await file.text();
      } else {
        toast.warning("仅支持 .docx / .txt / .md 文件");
        return;
      }
      content = content.trim();
      if (!content) {
        toast.warning("文件内容为空");
        return;
      }
      applyText(content, { scriptSource: "file", scriptSourceLabel: file.name });
      toast.success(`已导入「${file.name}」（${content.length} 字）`);
    } catch (e) {
      console.error("[ScriptToStoryboard] 解析文件失败:", e);
      toast.error("文件解析失败，请检查文件格式");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // 项目剧本：选一集或全部（剧集列表自带 scriptContent，无需额外端点）
  const handleEpisodeChoice = (value) => {
    setEpisodeChoice(value);
    persist({ scriptEpisodeId: value || null });
    if (!value) return;
    if (value === "all") {
      const all = (episodes || [])
        .filter((ep) => (ep?.scriptContent || "").trim())
        .map((ep) => `【${episodeLabel(ep)}】\n${ep.scriptContent.trim()}`)
        .join("\n\n");
      if (!all.trim()) {
        toast.warning("项目各集暂无剧本内容");
        return;
      }
      applyText(all, { scriptSource: "project", scriptSourceLabel: "全部集数" });
      toast.success(`已合并全部集数剧本（${all.length} 字）`);
      return;
    }
    const ep = (episodes || []).find((e) => String(e.id) === String(value));
    if (!ep) return;
    const content = (ep.scriptContent || "").trim();
    if (!content) {
      toast.warning("该集暂无剧本内容");
      return;
    }
    applyText(content, { scriptSource: "project", scriptSourceLabel: episodeLabel(ep) });
    toast.success(`已载入${episodeLabel(ep)}（${content.length} 字）`);
  };

  // 已连入参考（NodeCanvas 注入 connectedImages：{url, name, assetType}）
  const refGroups = { character: [], scene: [], prop: [], other: [] };
  (data.connectedImages || []).forEach((img) => {
    const key = ["character", "scene", "prop"].includes(img.assetType) ? img.assetType : "other";
    refGroups[key].push(img);
  });
  const refTotal = (data.connectedImages || []).length;

  const canSmartMatch = !!text.trim() && !hasActiveTask && !isGenerating && !smartMatching && !!projectId;
  const canGenMaster = !!text.trim() && !hasActiveTask && !isGenerating;
  const canGenConnecting = !hasActiveTask && !isGenerating && masterCount > 0;
  const canSyncShotReferences = !hasActiveTask && !isGenerating && !syncingShotReferences && masterCount > 0;
  const canArrangeStoryboard = !isGenerating && masterCount > 0;

  const btnBase = "flex items-center gap-1 px-2.5 h-7 rounded-md text-2xs font-medium whitespace-nowrap transition-colors";

  return (
    <div
      className="nodrag nowheel rounded-lg"
      style={{
        background: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border-primary)",
        overflow: "hidden",
        marginTop: 6,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* 生成进度 */}
      {isGenerating && (
        <div className="px-3 pt-2.5">
          <div className="flex items-center gap-1.5">
            <Loader2 size={10} className="animate-spin shrink-0" style={{ color: ACCENT }} />
            <span className="text-2xs truncate" style={{ color: "var(--color-text-secondary)" }}>
              {data.generatingStatus || "生成中..."}
            </span>
            {progress != null && (
              <span className="text-2xs font-mono-num ml-auto shrink-0" style={{ color: "var(--color-text-muted)" }}>
                {progress}%
              </span>
            )}
          </div>
          <div className="h-1 mt-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-bg-tertiary)" }}>
            <div
              style={{
                width: `${progress != null ? progress : 15}%`,
                height: "100%",
                background: ACCENT,
                transition: "width 0.4s ease",
              }}
            />
          </div>
        </div>
      )}
      {taskPaused && (
        <div
          className="mx-3 mt-2 flex items-center gap-2 px-2.5 py-2 rounded-md"
          style={{
            color: "var(--color-warning)",
            background: "color-mix(in srgb, var(--color-warning) 10%, transparent)",
            border: "1px solid color-mix(in srgb, var(--color-warning) 35%, transparent)",
          }}
        >
          <span className="text-2xs min-w-0 flex-1 truncate">
            后台任务仍在运行，上次查询已中断
          </span>
          <button
            onClick={() => data.onResumeStoryboardTask?.(id)}
            className="flex items-center gap-1 h-6 px-2 rounded text-2xs font-medium shrink-0"
            style={{ color: "var(--color-warning)", background: "var(--color-bg-secondary)" }}
          >
            <PlayCircle size={11} />
            继续查询
          </button>
        </div>
      )}
      {!isGenerating && !hasActiveTask && data.generationError && (
        <div
          className="mx-3 mt-2 px-2.5 py-2 rounded-md text-2xs line-clamp-2"
          style={{
            color: "var(--color-danger)",
            background: "color-mix(in srgb, var(--color-danger) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--color-danger) 25%, transparent)",
          }}
          title={data.generationError}
        >
          {data.generationError}
        </div>
      )}

      {/* ── 文本来源 ── */}
      <div className="px-3 pt-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-label">剧本来源</span>
          {data.scriptSourceLabel && (
            <span className="text-2xs truncate ml-2" style={{ color: "var(--color-text-muted)" }} title={data.scriptSourceLabel}>
              {data.scriptSourceLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 mb-2">
          {TABS.map((item) => (
            <button
              key={item.key}
              onClick={() => switchTab(item.key)}
              className="flex-1 flex items-center justify-center gap-1 h-6 rounded-md text-2xs font-medium transition-colors"
              style={{
                background: tab === item.key ? accentBg(ACCENT) : "transparent",
                color: tab === item.key ? ACCENT : "var(--color-text-muted)",
              }}
            >
              <item.Icon size={10} />
              {item.label}
            </button>
          ))}
        </div>

        {tab === "input" && (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => persist({ scriptText: text })}
            className="w-full text-xs bg-transparent resize-none outline-none leading-relaxed custom-scrollbar"
            style={{ color: "var(--color-text-primary)", minHeight: 96, maxHeight: 220, overflow: "auto" }}
            placeholder="粘贴或输入剧本正文..."
          />
        )}

        {tab === "file" && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".docx,.txt,.md"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={parsing}
              className="w-full flex flex-col items-center justify-center gap-1.5 py-4 rounded-md transition-colors"
              style={{
                border: "1px dashed var(--color-border-primary)",
                color: "var(--color-text-muted)",
                background: "var(--color-bg-tertiary)",
                cursor: parsing ? "wait" : "pointer",
              }}
            >
              {parsing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              <span className="text-2xs">{parsing ? "解析中..." : "点击选择 .docx / .txt / .md"}</span>
            </button>
            {text && (
              <div className="mt-1.5 text-2xs line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                已载入 {text.length} 字：{text.slice(0, 60)}...
              </div>
            )}
          </div>
        )}

        {tab === "project" && (
          <div>
            {epLoading ? (
              <div className="flex items-center gap-1.5 py-3 justify-center">
                <Loader2 size={12} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
                <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>加载分集列表...</span>
              </div>
            ) : episodes === null ? (
              <button onClick={loadEpisodes} className={btnBase} style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
                加载项目分集
              </button>
            ) : episodes.length === 0 ? (
              <div className="py-3 text-center text-2xs" style={{ color: "var(--color-text-muted)" }}>当前项目暂无分集</div>
            ) : (
              <AppSelect
                value={episodeChoice}
                onChange={(e) => handleEpisodeChoice(e.target.value)}
                className="w-full h-7 px-2 rounded-md text-xs outline-none custom-scrollbar"
                style={{
                  background: "var(--color-bg-tertiary)",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-border-primary)",
                }}
              >
                <option value="">选择集数...</option>
                <option value="all">全部集数（合并）</option>
                {episodes.map((ep) => (
                  <option key={ep.id} value={String(ep.id)}>
                    {episodeLabel(ep)}{(ep.scriptContent || "").trim() ? "" : "（无剧本）"}
                  </option>
                ))}
              </AppSelect>
            )}
            {text && (
              <div className="mt-1.5 text-2xs line-clamp-2" style={{ color: "var(--color-text-muted)" }}>
                已载入 {text.length} 字：{text.slice(0, 60)}...
              </div>
            )}
          </div>
        )}

        {tab === "input" && (
          <div className="flex justify-end mt-0.5">
            <span className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>{text.length} 字</span>
          </div>
        )}
      </div>

      {/* ── 参考图区 + 智能匹配 ── */}
      <div className="px-3 pt-2 pb-1">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-label">参考资产{refTotal > 0 ? `（${refTotal}）` : ""}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => data.onSyncShotReferences?.(id)}
              disabled={!canSyncShotReferences}
              className={btnBase}
              style={{
                background: canSyncShotReferences ? "var(--color-bg-tertiary)" : "transparent",
                color: canSyncShotReferences ? "var(--color-text-secondary)" : "var(--color-text-muted)",
                cursor: canSyncShotReferences ? "pointer" : "not-allowed",
              }}
              title="为已有主镜和连接镜补齐缺失的资产参考；保留手工添加的参考，不自动删除"
            >
              {syncingShotReferences
                ? <Loader2 size={11} className="animate-spin" />
                : <RefreshCw size={11} />}
              同步镜头参考
            </button>
            <button
              onClick={() => data.onSmartMatch?.(id)}
              disabled={!canSmartMatch}
              className={btnBase}
              style={{
                background: canSmartMatch ? accentBg(ACCENT, 15) : "var(--color-bg-tertiary)",
                color: canSmartMatch ? ACCENT : "var(--color-text-muted)",
                cursor: canSmartMatch ? "pointer" : "not-allowed",
              }}
              title="从角色/场景/道具工坊按名称精确匹配剧本，自动创建参考节点并连线"
            >
              {smartMatching ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
              智能匹配
            </button>
          </div>
        </div>
        {refTotal > 0 ? (
          <div className="flex flex-col gap-1.5">
            {Object.entries(refGroups).map(([type, imgs]) => imgs.length > 0 && (
              <div key={type} className="flex items-center gap-1.5">
                <span className="text-2xs shrink-0 w-7" style={{ color: ASSET_TYPE_COLOR[type] }}>
                  {ASSET_TYPE_LABEL[type]}
                </span>
                <div className="flex gap-1 overflow-x-auto custom-scrollbar">
                  {imgs.map((img, i) => (
                    <div
                      key={i}
                      className="w-9 h-9 rounded-md overflow-hidden shrink-0"
                      style={{ border: "1px solid var(--color-border-primary)" }}
                      title={img.name || ""}
                    >
                      <img src={img.url} alt={img.name || ""} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-2xs py-1" style={{ color: "var(--color-text-muted)" }}>
            从左侧连入角色/场景/道具参考图，或点击「智能匹配」自动查找
          </div>
        )}
      </div>

      {/* ── 生成区 ── */}
      <div className="px-3 pt-1.5 pb-2.5">
        <div className="flex items-center gap-1 flex-wrap mb-2">
          <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>画幅</span>
          {ASPECT_RATIOS.map((r) => (
            <button
              key={r}
              onClick={() => {
                setAspectRatio(r);
                persist({ aspectRatio: r });
              }}
              className="px-1.5 h-5 rounded text-2xs font-medium font-mono-num whitespace-nowrap transition-colors"
              style={{
                background: aspectRatio === r ? accentBg(ACCENT) : "transparent",
                color: aspectRatio === r ? ACCENT : "var(--color-text-muted)",
              }}
            >
              {r}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => data.onGenerateMasterShots?.(id, { aspectRatio })}
            disabled={!canGenMaster}
            className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-bold transition-colors"
            style={{
              background: canGenMaster ? ACCENT : "var(--color-bg-tertiary)",
              color: canGenMaster ? "var(--color-accent-contrast)" : "var(--color-text-muted)",
              cursor: canGenMaster ? "pointer" : "not-allowed",
            }}
            title="按方法论拆主镜：戏点分析 → 位置关系 → 优中选优"
          >
            {isGenerating ? <Loader2 size={13} className="animate-spin" /> : <Film size={13} />}
            生成故事板粗稿{masterCount > 0 ? `（已有 ${masterCount}）` : ""}
          </button>
          <button
            onClick={() => data.onGenerateConnectingShots?.(id)}
            disabled={!canGenConnecting}
            className="flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-bold transition-colors"
            style={{
              background: canGenConnecting ? accentBg(ACCENT, 15) : "var(--color-bg-tertiary)",
              color: canGenConnecting ? ACCENT : "var(--color-text-muted)",
              border: `1px solid ${canGenConnecting ? ACCENT : "var(--color-border-secondary)"}`,
              cursor: canGenConnecting ? "pointer" : "not-allowed",
            }}
            title={masterCount > 0 ? "按三种作用评估连接镜（能不加就不加）" : "请先生成故事板粗稿"}
          >
            <Link2 size={13} />
            补必要连接镜
          </button>
          <button
            onClick={() => data.onCreateBlankStoryboardShot?.(id, { aspectRatio })}
            disabled={isGenerating || hasActiveTask}
            className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 transition-colors"
            style={{
              background: "var(--color-bg-tertiary)",
              color: isGenerating || hasActiveTask ? "var(--color-text-muted)" : ACCENT,
              border: "1px solid var(--color-border-secondary)",
              cursor: isGenerating || hasActiveTask ? "not-allowed" : "pointer",
            }}
            title="新建空白故事板镜头，完全由人工填写"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex items-center justify-between mt-1.5 gap-2">
          <button
            onClick={() => data.onArrangeStoryboard?.(id)}
            disabled={!canArrangeStoryboard}
            className="flex items-center gap-1 px-2 h-6 rounded-md text-2xs transition-colors"
            style={{
              color: canArrangeStoryboard ? "var(--color-text-secondary)" : "var(--color-text-muted)",
              background: "var(--color-bg-tertiary)",
              cursor: canArrangeStoryboard ? "pointer" : "not-allowed",
            }}
            title="把本节点生成的故事板镜头按叙事顺序整理为可整体移动的纵向序列"
          >
            <Rows3 size={11} />
            纵向成组
          </button>
          <button
            onClick={onClose}
            className="text-2xs px-2 h-6 rounded-md transition-colors"
            style={{ color: "var(--color-text-muted)", background: "var(--color-bg-tertiary)" }}
          >
            完成
          </button>
        </div>
      </div>
    </div>
  );
}
